import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TaxRegionKey } from '@capintel/instruments-lib';

import { updateComparisonTaxSettings } from '@/redux/modules/comparison';
import { ReduxState } from '@/redux/modules/types';
import { PartialUsComparisonTaxSettings } from '@/types/tax-analysis';
import { US_DEFAULT_SHORT_TERM_CAPITAL_GAINS_RATE, US_DEFAULT_LONG_TERM_CAPITAL_GAINS_RATE } from '../../constants';
import { Dispatch } from '@/redux';
import { FIELD_KEYS } from './constants';

const US_FIELD_KEYS = FIELD_KEYS[TaxRegionKey.US];

/** Default US tax settings */
const DEFAULT_US_SETTINGS: PartialUsComparisonTaxSettings = {
  shortTermCapitalGainsRate: US_DEFAULT_SHORT_TERM_CAPITAL_GAINS_RATE * 100,
  longTermCapitalGainsRate: US_DEFAULT_LONG_TERM_CAPITAL_GAINS_RATE * 100,
};

export const useTaxAnalysisInputsUS = () => {
  const dispatch = useDispatch<Dispatch>();
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const initialized = useRef(false);

  const taxSettings = activeComparison?.taxAnalysis?.taxSettings;
  const usSettings = taxSettings?.[TaxRegionKey.US] ?? {};
  const defaultUsSettings: PartialUsComparisonTaxSettings = { ...DEFAULT_US_SETTINGS, ...usSettings };

  // Initialize defaults in Redux once
  useEffect(() => {
    if (!initialized.current && activeComparison) {
      dispatch(updateComparisonTaxSettings({ region: TaxRegionKey.US, [TaxRegionKey.US]: defaultUsSettings }));
      initialized.current = true;
    }
  }, [activeComparison, dispatch, defaultUsSettings]);

  // Create setters that update the US-specific settings
  const createUsSetter = useCallback(
    (key: keyof PartialUsComparisonTaxSettings) => (value?: number) => {
      if (!activeComparison) return;
      const updatedSettings: PartialUsComparisonTaxSettings = { ...usSettings, [key]: value };
      dispatch(updateComparisonTaxSettings({ region: TaxRegionKey.US, [TaxRegionKey.US]: updatedSettings }));
    },
    [activeComparison, dispatch, usSettings],
  );

  return {
    values: usSettings,
    setShortTermYTDGain: createUsSetter(US_FIELD_KEYS.SHORT_TERM_YTD_GAIN),
    setShortTermYTDLoss: createUsSetter(US_FIELD_KEYS.SHORT_TERM_YTD_LOSS),
    setLongTermYTDGain: createUsSetter(US_FIELD_KEYS.LONG_TERM_YTD_GAIN),
    setLongTermYTDLoss: createUsSetter(US_FIELD_KEYS.LONG_TERM_YTD_LOSS),
    setShortTermCarriedForwardLoss: createUsSetter(US_FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS),
    setLongTermCarriedForwardLoss: createUsSetter(US_FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS),
    setShortTermCapitalGainsRate: createUsSetter(US_FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE),
    setLongTermCapitalGainsRate: createUsSetter(US_FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE),
  };
};
